import type { LngLat } from 'ymaps3'

import type { E_CertificateTypes } from '@/enums'


//
// Данные юзера
//

export interface T_UserBalanceRes {
  list: {
    id: number
    title: string
    account_type: number
    currency: number
    value: {
      currency: { id: number }
      value: string
      format: string
    }
    blocked: {
      currency: { id: number }
      value: string
      format: string
    }
    amount: {
      currency: { id: number }
      value: string
      format: string
    }
    visitor_group: number[] | null
    header_visitor_group: number[] | null
    is_header: boolean
    is_content: boolean
    is_topup: boolean
    is_withdrawal: boolean
  }[]
}

export type T_UserCard = {
  id: number
  type: number
  title: { ru: string }
  flags: { blocked?: boolean } | null
}

export type T_UserCardsListRes = T_UserCard[]

export type T_UserCardBalanceRes = {
  balance: string
  is_actual: boolean
}

export interface T_UserDataRes {
  id: number
  login: string
  login2: string | null
  fullname: string | null
  lastname: string | null
  firstname: string | null
  patronymic: string | null
  gender: number
  email: string | null
  phone: string | null
  mobile: string | null
  locality: number
  address: string | null
  mail_index: string | null
  visitor_date: string | null
  birth_date: string | null
  photo: string | null
  status: {
    career: number
    status: number
    source_company_id: number
  }
  object: number
  properties: Record<string, any>
  visitor_group: number[]
  privileges: string[]
  // Это Refresh-токен, которым можно обновить сессионную авторизационную куку SID.
  // Для этого нужно сделать с этим токеном запрос на client/visitor/authorize_token
  token: string
}

export type T_UsersListForAutocompleteListWhenSearchRes = T_UserForAutocompleteListWhenSearchRes[]

interface T_UserForAutocompleteListWhenSearchRes {
  firstname: string
  status: {
    career: number
    status: number
    source_company_id: number
  }
  login: string
  type: number
  patronymic: string
  currency: number
  id: number
  level: number
  object: number
  email: string
  locality: number
  mobile: string
  parent: number
  lastname: string
}

export interface T_VisitorLinkRes {
  list: {
    id: number
    title: string
    link: string
  }[]
}

export type T_StoreGoodsListRes = {
  list: {
    goods: {
      id: number
      code: string
      name: string
      path: {
        id: string
        name: string
        title: string
      }[]
      type: {
        id: number
      }
      category: {
        id: number
        title: string
      }
      photo: {
        id: number
        path: string
      }
      photo_list: any[]
      title: string
      description: string | null
      content: string | null
      content2: string | null
      video: string | null
      meta_title: string | null
      meta_description: string | null
      bound: {
        min: number
        max: number
        scale: number
      }
      mass: number
      agreement_article_list: any[]
      is_excluded_from_store_accounting: boolean
      is_recommend: boolean
      is_new: boolean
      is_promo: boolean
      is_coming_soon: boolean
      is_delivery_required: boolean
      properties: {
        personal_bonus: string
        binary_places: string
        binary_points: string
        binary_private: string
      }
    }
    price: {
      currency: {
        id: number
      }
      value: string
      format: string
    }
    discount: {
      currency: {
        id: number
      }
      value: string
      format: string
    }
    amount: {
      currency: {
        id: number
      }
      value: string
      format: string
    }
    points: number
    price_map: {
      base: {
        rur: {
          currency: {
            id: number
          }
          value: string
          format: string
        }
        pie: {
          currency: {
            id: number
          }
          value: string
          format: string
        }
      }
    }
  }[]
  hits: number
}


//
// Структура
//

export interface T_StructureListRes {
  level: number
  list: T_StructureNode[]
}

export type T_StatusTitles = '-' | 'Тариф 3000' | 'Тариф 10000' | 'Тариф 30000'

export interface T_StructureNode {
  id: number
  login: string
  photo: {
    id: number
    path: string
  }
  has_children: boolean
  fullname: string
  lastname: string | null
  firstname: string | null
  patronymic: string | null
  phone: string | null
  mobile: string | null
  email: string | null
  status: {
    id: number
    title: T_StatusTitles
  }
  career: {
    id: number
    title: string
  }
  source_company_id: {
    id: number
    title: string
  }
}

export interface T_StructureInfoRes {
  level: number
  id: number
  login: string
  photo: {
    id: number
    path: string
  } | null
  parent: {
    id: number
    login: string
  }
  info: {
    parent_login: string
    visitor_date: string
    locality: number
    object: {
      id: number
      title: string
    }
  }
  fullname: string
  lastname: string | null
  firstname: string | null
  patronymic: string | null
  phone: string | null
  mobile: string | null
  email: string | null
  status?: {
    id: number
    title: T_StatusTitles
  }
  props: {
    type: string
    title: string
    value: number[]
  }[]
}


//
// Пайщик
//

export type T_ResidentsListRes = {
  id: number
  title: { ru: string }
}[]


//
// Сертификаты
//

export enum E_CodeTypes {
  EAN13 = 'ean13',
  CODE128 = 'code128',
  QRCode = 'qrcode',
}

export type T_CertificatesListRes = {
  id: number
  code: string
  document?: number
  goods: number // It's certificate nominal id
  time: number
  type: E_CodeTypes
}[]

export type T_CertificateCategoriesListRes = {
  id: number
  code: string | null
  name: E_CertificateTypes
  parent: number | null
  title: { ru: string }
  visitor_group: number[]
}[]

export type T_CertificateNominalsListRes = {
  id: number
  price: {
    base: { rur: number }
  }
  name: string
  unit: number
  category: number
  flags: number
  points: { base: number }
  type: number
  title: { ru: string }
}[]

export type T_CertificatesRestListRes = number[][]


//
// Заправки
//

export enum E_FuelType {
  A80 = 'a80',
  A92 = 'a92',
  A92_Premium = 'a92_premium',
  A92_Opti = 'a92_opti',
  A95 = 'a95',
  A95_Premium = 'a95_premium',
  A95_Opti = 'a95_opti',
  A98 = 'a98',
  A98_Premium = 'a98_premium',
  A100 = 'a100',
  A100_Premium = 'a100_premium',
  Diesel = 'diesel',
  Diesel_Premium = 'diesel_premium',
  Diesel_Opti = 'diesel_opti',
  Diesel_Opti_Winter = 'diesel_opti_winter',
  Diesel_Winter = 'diesel_winter',
  Diesel_Ecto = 'dieselEcto',
  Diesel_R = 'dieselR',
  Diesel_Demiseason = 'diesel_demiseason',
  Propane = 'propane',
  Gas = 'gas',
  Kerosene = 'kerosene',
  Metan = 'metan',
  Methane = 'methane',
  Metan_Premium = 'metan_premium',
}

export interface T_Pump {
  id: number
  fuels: E_FuelType[]
}

export type T_FuelPrices = Partial<Record<E_FuelType, LngLat>>

export interface T_GasStation {
  code?: string
  enable?: boolean
  postpay?: boolean
  takeBefore?: boolean
  orderBefore?: boolean
  maxAmount?: string
  pumps?: T_Pump[]
  prices: T_FuelPrices
  title: string
  location: [ number, number ]
  region?: number
  id?: number
  address: string
  photo?: string
  distance?: number
}

export interface T_Station {
  id: number
  code: string
  title: string
  address: string
  location: LngLat
  region: number
  enable: boolean
  postpay: boolean
  take_before: boolean
  order_before: boolean
  max_amount: string
  pumps?: T_Pump[]
  prices?: T_FuelPrices
  fuels?: E_FuelType[]
}

export interface T_ClusterWithStations {
  id: string
  isCluster: true
  location: LngLat
  count: number
}

export interface T_OrderStatusRes {
  targetVolume: number
  targetPrice: number
  volume: number
  id: number
  pump: number
  station: number
  fuel: E_FuelType
  price: number
  state: string
}

export interface T_FuelData {
  price: [ number, number ]
  is_taken: boolean
}

export type T_PumpInfoItem = {
  id: number
  locked: boolean
  fuels: Record<E_FuelType, T_FuelData>
}

export type T_PumpInfoRes = T_PumpInfoItem[]

export interface T_FuelInfoItem {
  id: number
  title: string
  short_title: string
  long_title: string
  code: string
}

export type T_FuelInfoRes = T_FuelInfoItem[]


//
// Транзакции
//

export interface T_TransactionsListRes {
  list: T_Transaction[]
}

export type T_Transaction = {
  id: number
  time: {
    value: number
    date: string
    time: string
    format: string
  }
  expense?: {
    currency: {
      id: number
    }
    value: string
    format: string
  }
  receipt?: {
    currency: {
      id: number
    }
    value: string
    format: string
  }
  contractor: {
    id: number
    login: string
    fullname: string
    system: boolean
  }
  description: string
  snapshot: {
    currency: {
      id: number
    }
    value: string
    format: string
  }
}


//
// Платежи
//

export interface T_PaymentInfoRes {
  currency: string
  value: string
  paid: string
  agreements: {
    goods: any[]
  },
  documents: {
    id: number
    count: number
    price: number
    discount: number
    delivery: number
  }[]
  goods: {
    id: number
    count: number
    value: string
    points: number
    methods: string[]
  }[]
  methods: {
    id: string
    category: number
    title: string
    image: null
    currency: string
    rate: string
    value: string[]
    comission: string[]
    points: boolean
    balance: string
  }[]
  scenarios: {
    id: number
    title: string
    description: null
    image: null
    rules: {
      id: number
      methods: string[]
      value: string[]
    }[]
  }[]
}

export interface T_MoneyAccountPaymentRequestRes {
  request: number
  process: {
    type: string
    protection: boolean
    data: {
      request: {
        id: string
        currency: string
        amount: string
        create_date: string
        expire_date: string
      }
      note: string
      qrcode: {
        content: string
        link: string
      }
      bank: {
        name: string
        bic: string
        account: string
      }
      company: {
        name: string
        address: string
        account: string
        inn: string
        kpp: string
        ogrn: string
        nds: string
      }
    }
    link: string
    print: string
  }
}


//
// Техническая поддержка
//

export interface T_TicketsListRes {
  list: {
    id: number
    time: {
      value: number
      date: string
      time: string
      format: string
    }
    subject: string
    category: {
      id: number
      title: string
    }
    priority: {
      id: number // 100, 200, 300
      title: string
    }
    status: {
      id: number
      title: string
    }
    unread: boolean
  }[]
}

export interface T_TicketsCategoriesListRes {
  list: {
    id: number
    title: string
  }[]
}

export interface T_TicketAttachmentFile {
  id?: number
  title?: string
  path?: string
}

export interface T_TicketConversationRes {
  list: {
    id: number
    time: {
      value: number
      date: string
      time: string
      format: string
    },
    message: string
    files: T_TicketAttachmentFile[]
    visitor: {
      id: number
      title: string
    }
    unread: boolean
  }[]
}


//
// Документы
//

export type T_CategoryResponse = {
  description: {
    ru: string
  }
  name: string
  title: {
    ru: string
  }
  children: number
}

export type T_DocumentResponse = {
  name: string
  title: {
    ru: string
  }
  description?: {
    ru: string
  }
  link?: {
    ru: string
  }
  content?: {
    ru: string
  }
  children?: 0
}

export type T_CategoriesResponse = T_CategoryResponse[]

export type T_DocsResponse = T_DocumentResponse[]

//
// Адреса
//

export type T_NameTuple = [ string | null, string ]

export interface T_AddressLocalityItem {
  geoname?: string
  fias?: string
  kladr?: string
  location?: [ number, number ]
  country?: [ string, string ]
  region: T_NameTuple
  city?: T_NameTuple
  id: number
  value?: string[]
  timezone?: string
  zip?: string
}

export type T_AddressLocalitySuggestRes = T_AddressLocalityItem[]


//
// Новости
//

export type T_NewsListRes = T_NewsListItem[]

export interface T_NewsListItem {
  video: {
    ru: string
  }
  type: number
  path: string
  time: number
  flags: number
  id: number
  title2: {
    ru: string
  }
  name: string
  title: {
    ru: string
  }
  parent: number
  link: {
    ru: string
  }
}

export type T_NewsArticleRes = T_NewsArticle[]

export interface T_NewsArticle {
  id: number
  content: {
    ru: string
  }
  video: {
    ru: string
  }
  path: string
  name: string
  time: number
  title: {
    ru: string
  }
  description: {
    ru: string
  }
}
